<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, computed } from 'vue';
import { useRoute, ToastTypes } from '@hfc/ui-core';
import { appConfig } from '@/../AppConfig';
import { ApplicationSection } from '@/shared/application/ApplicationSection';
import { MedicareAgent } from '@/consent/medicare/domain/MedicareAgent';
import { useSectionNavigation } from '@/shared/infrastructure/composition/SectionNavigation';
import { useHandleApiError } from '@/shared/infrastructure/composition/useHandleApiError';
import SectionInitials from '@/consent/medicare/infrastructure/components/sections/SectionInitials.vue';
import SectionAppointment from '@/consent/medicare/infrastructure/components/sections/SectionAppointment.vue';
import SectionSignature from '@/consent/medicare/infrastructure/components/sections/SectionSignature.vue';
import SectionThanks from '@/consent/medicare/infrastructure/components/sections/SectionThanks.vue';
import ProgressBar from '@/application/shared/infrastructure/components/ProgressBar.vue';
import AppNavigation from '@/application/shared/infrastructure/components/AppNavigation.vue';
import SectionContactPreferences from '@/consent/medicare/infrastructure/components/sections/SectionContactPreferences.vue';
import GoogleRecaptcha from '@/shared/infrastructure/components/GoogleRecaptcha.vue';
import ConsentNavigationModals from '@/consent/medicare/infrastructure/components/ConsentNavigationModals.vue';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';
import { useMedicareConsentStore } from '@/consent/medicare/infrastructure/composition/useMedicareConsentStore';

const { appStore } = useAppStore();
const { medicareConsentStore, medicareConsentState } = useMedicareConsentStore();

const route = useRoute();
const { handleServicesApiError } = useHandleApiError();
const agent = appConfig.agentCollection.getAgent(route.params.agent as string);

const props = defineProps<{
  appointment?: boolean
  appointmentKey?: string
}>();

const appointmentLink = ():string => {
  if (route.params.agent) {
    return agent[props.appointmentKey as keyof MedicareAgent] ?? agent.appointmentLink;
  }

  return appConfig.agentCollection.getRoundRobinLink(medicareConsentState.value.application.contact_preference);
};

const recaptchaRef = ref<InstanceType<typeof GoogleRecaptcha> | null>(null);

interface MedicareSection extends ApplicationSection {
  validateOnSaveAndExit?: boolean;
  skipNextOnSubmit?: boolean;
}

const sectionList:Array<MedicareSection> = [
  {
    component: SectionContactPreferences,
    props: {
      title: props.appointment ? 'Schedule an Appointment' : 'Contact Info',
      showContactPreference: () => medicareConsentState.value.application.appointment_required === true && !route.params.agent
    },
    shortTitle: 'Contact',
    validateOnSaveAndExit: true
  },
  {
    component: SectionInitials,
    props: {},
    shortTitle: 'Topic'
  },
  {
    component: SectionSignature,
    shortTitle: props.appointment ? 'Consent' : 'Signature',
    recaptchaEnabled: true,
    validateOnSaveAndExit: true
  },
  {
    component: SectionAppointment,
    props: {
      appointmentLink,
      recaptchaTokenRequired: true
    },
    conditionalShow: (): boolean => medicareConsentState.value.application.appointment_required === undefined ? true : medicareConsentState.value.application.appointment_required,
    shortTitle: 'Schedule',
    icon: 'calendar-day',
    nextHidden: (): boolean => true,
    skipNextOnSubmit: true
  },
  {
    component: SectionThanks,
    props: {},
    conditionalShow: (): boolean => !props.appointment,
    shortTitle: 'Thanks'
  },
];

const stepRef = ref<InstanceType<typeof SectionInitials> | null>(null);
const sectionLoading = ref(false);
const pendingAction = ref<(() => void) | null>(null);
const awaitingCaptcha = ref(false);
const sectionRequiresCaptcha = computed(() => filteredSectionList.value[currentSection.value].recaptchaEnabled);
const navModalsRef = ref<InstanceType<typeof ConsentNavigationModals> | null>(null);
const hideNavigation = ref(false);

const {
  showProgressBar,
  filteredSectionList,
  currentSection,
  nextSection,
  prevSection,
  goToSection,
  stepToLastIncompleteSection,
  resetSection
} = useSectionNavigation(sectionList, stepRef, props.appointment);

const submitForm = async (): Promise<void> => {
  const sectionIsValid = await stepRef.value?.sectionSubmit();

  if (sectionIsValid) {
    const result = await medicareConsentStore.submit();
    if (result.success) {
      if (!filteredSectionList.value[currentSection.value].skipNextOnSubmit) {
        nextSection();
      }

      return;
    }

    if (result.error) {
      handleServicesApiError(result.error, true);
    }
  }
};

const submitCalendlyInfo = async (): Promise<void> => {
  const result = await medicareConsentStore.updateReferenceId();

  if (result.error) {
    handleServicesApiError(result.error, true);
  }
};

const handleCaptchaSuccess = (token: string | null): void => {
  awaitingCaptcha.value = false;

  if (!token) {
    return;
  }

  medicareConsentStore.updateForm({
    captcha_token: token
  });

  if (pendingAction.value) {
    pendingAction.value();
    pendingAction.value = null;
  }
};

const checkRecaptcha = (callback: () => void): void => {
  if (sectionRequiresCaptcha.value && recaptchaRef.value && !medicareConsentState.value.application.captcha_token) {
    awaitingCaptcha.value = true;
    pendingAction.value = callback;
    recaptchaRef.value.executeRecaptcha();

    return;
  }

  callback();
};

const checkValidToSaveAndExit = async (): Promise<void> => {
  if (filteredSectionList.value[currentSection.value].validateOnSaveAndExit) {
    const sectionIsValid = await stepRef.value?.sectionSubmit();
    if (!sectionIsValid) {
      appStore.addToast({ type: ToastTypes.DANGER, message: 'Please enter form data before saving.' });

      return;
    }
  }
  if (typeof stepRef.value?.sectionIsValid === 'function') {
    stepRef.value.sectionIsValid();
  }

  navModalsRef.value?.open();
};

const checkIfLoadedFromDb = (): void => {
  if (medicareConsentState.value.loadingFromDb) {
    stepToLastIncompleteSection(2);
  }
};

const applyRouteConfig = (): void => {
  medicareConsentStore.updateForm({ appointment_required: props.appointment, agent: route.params.agent as string, aep: props.appointmentKey ? true : false });
};

const clearAndRestart = (): void => {
  medicareConsentStore.clearAndRestart();
  applyRouteConfig();
  resetSection();
};

nextTick(async () => {
  await appStore.updateCustomTitle(props.appointment ? 'Medicare Appointment' : 'Medicare Consent');
  await appStore.setShowFooter(false);
  applyRouteConfig();
  checkIfLoadedFromDb();
});

onMounted(() => {
  recaptchaRef.value?.renderRecaptcha();
});

onBeforeUnmount(async () => {
  await appStore.updateCustomTitle('');
  await appStore.setShowFooter(true);
});
</script>

<template>
  <div class="flex items-start flex-col flex-grow mb-3" v-highlight="{ multipleCoincidences: true }" :key="medicareConsentState.application.id">
    <div class="py-3 w-full">
      <ProgressBar :current-section="currentSection" v-if="showProgressBar" :section-list="filteredSectionList" :show-last-step-in-flow="appointment" @goToSection="goToSection"></ProgressBar>
    </div>
    <div class="flex-grow flex flex-col w-full">
      <div class="w-full flex flex-col">
        <component :is="filteredSectionList[currentSection].component" ref="stepRef" v-bind="filteredSectionList[currentSection].props" @prevSection="prevSection" @setLoading="(v: boolean) => sectionLoading = v" @goToSection="goToSection" @hideNavigation="hideNavigation = true" @submitConsent="checkRecaptcha(submitForm)" @submitCalendlyInfo="checkRecaptcha(submitCalendlyInfo)"></component>
      </div>
      <AppNavigation v-if="!hideNavigation" :current-step="currentSection" @submit="checkRecaptcha(submitForm)" :total-steps="filteredSectionList.length - 1" :show-last-step-in-flow="appointment" :next-hidden="filteredSectionList[currentSection].nextHidden ? [currentSection] : undefined" @prevSection="prevSection" @nextSection="checkRecaptcha(nextSection)" :loading="sectionLoading || awaitingCaptcha || medicareConsentState.isSubmitting">
        <template #after-continue>
          <ConsentNavigationModals
            ref="navModalsRef"
            :current-section="currentSection"
            @save-and-exit="checkValidToSaveAndExit"
            @clear-and-restart="clearAndRestart()" />
        </template>
        <template #after-submit>
          <ConsentNavigationModals
            ref="navModalsRef"
            :current-section="currentSection"
            @save-and-exit="checkValidToSaveAndExit"
            @clear-and-restart="clearAndRestart()" />
        </template>
      </AppNavigation>
    </div>
  </div>
  <GoogleRecaptcha container-id="recaptcha-container-medicare-consent" @captcha-success="handleCaptchaSuccess" ref="recaptchaRef" />
</template>
